import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import { TextField, Button } from '../../primitives';
import { getCsrfToken } from '../../../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const RADIUS_OPTIONS = [1, 5, 10, 15, 20, 30, 50, 100];

/**
 * AddressForm - Reusable address form dialog component
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Callback when dialog closes
 * @param {function} onSave - Callback when address is saved (receives saved address)
 * @param {object} editAddress - Address object to edit (null for new address)
 */
const AddressForm = ({ open, onClose, onSave, editAddress }) => {
  const [formData, setFormData] = useState({
    location_name: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    default_radius: 20,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Populate or reset form when modal opens/closes or editAddress changes
  useEffect(() => {
    if (open) {
      if (editAddress) {
        // Pre-populate form with existing address data
        setFormData({
          location_name: editAddress.location_name || '',
          address_1: editAddress.address_1 || '',
          address_2: editAddress.address_2 || '',
          city: editAddress.city || '',
          state: editAddress.state || '',
          zip_code: editAddress.zip_code || '',
          country: editAddress.country || 'United States',
          default_radius: editAddress.default_radius || 20,
        });
      } else {
        // Reset form for new address
        setFormData({
          location_name: '',
          address_1: '',
          address_2: '',
          city: '',
          state: '',
          zip_code: '',
          country: 'United States',
          default_radius: 20,
        });
      }
      setErrors({});
      setErrorMessage('');
    }
  }, [open, editAddress]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    // Clear general error message
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user changes selection
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    // Clear general error message
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setErrors({});

    try {
      const requestData = {
        ...formData,
        // Ensure optional fields are sent as empty strings if not provided
        address_2: formData.address_2 || '',
        country: formData.country || 'United States',
        default_radius: formData.default_radius || 20,
      };

      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const isEdit = !!editAddress;
      const url = isEdit 
        ? `${API_BASE_URL}/addresses/${editAddress.id}/`
        : `${API_BASE_URL}/addresses/`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        credentials: 'include',
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors
        if (data.location_name || data.address_1 || data.address_2 || data.city || data.state || data.zip_code || data.country) {
          setErrors(data);
        } else {
          const action = isEdit ? 'update' : 'create';
          setErrorMessage(data.error || data.message || `Failed to ${action} address. Please try again.`);
        }
        setLoading(false);
        return;
      }

      // Success - call parent callback with saved address
      onSave(data);
      // Reset form (will be handled by useEffect when dialog closes)
      setLoading(false);
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      console.error(`Error ${editAddress ? 'updating' : 'creating'} address:`, err);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editAddress ? 'Edit Address' : 'Create Address'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Location Name"
            name="location_name"
            value={formData.location_name}
            onChange={handleChange}
            error={!!errors.location_name}
            helperText={errors.location_name}
            required
            margin="normal"
            autoComplete="off"
            autoFocus
          />

          <TextField
            fullWidth
            label="Address 1"
            name="address_1"
            value={formData.address_1}
            onChange={handleChange}
            error={!!errors.address_1}
            helperText={errors.address_1}
            required
            margin="normal"
            autoComplete="street-address"
          />

          <TextField
            fullWidth
            label="Address 2"
            name="address_2"
            value={formData.address_2}
            onChange={handleChange}
            error={!!errors.address_2}
            helperText={errors.address_2}
            margin="normal"
            autoComplete="address-line2"
          />

          <TextField
            fullWidth
            label="City"
            name="city"
            value={formData.city}
            onChange={handleChange}
            error={!!errors.city}
            helperText={errors.city}
            required
            margin="normal"
            autoComplete="address-level2"
          />

          <TextField
            fullWidth
            label="State"
            name="state"
            value={formData.state}
            onChange={handleChange}
            error={!!errors.state}
            helperText={errors.state}
            required
            margin="normal"
            autoComplete="address-level1"
          />

          <TextField
            fullWidth
            label="Zip Code"
            name="zip_code"
            value={formData.zip_code}
            onChange={handleChange}
            error={!!errors.zip_code}
            helperText={errors.zip_code}
            required
            margin="normal"
            autoComplete="postal-code"
          />

          <FormControl fullWidth margin="normal" error={!!errors.country}>
            <InputLabel>Country</InputLabel>
            <Select
              name="country"
              value={formData.country}
              onChange={handleSelectChange}
              label="Country"
            >
              <MenuItem value="United States">United States</MenuItem>
            </Select>
            {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth margin="normal" error={!!errors.default_radius}>
            <InputLabel>Set default radius</InputLabel>
            <Select
              name="default_radius"
              value={formData.default_radius}
              onChange={handleSelectChange}
              label="Set default radius"
            >
              {RADIUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option} {option === 1 ? 'mile' : 'miles'}
                </MenuItem>
              ))}
            </Select>
            {errors.default_radius && <FormHelperText>{errors.default_radius}</FormHelperText>}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>Saving...</span>
              </Box>
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddressForm;
